import React, { useRef, useState } from 'react'
import CX from 'classnames'
import { Classes, Intent, Spinner, Tag } from '@blueprintjs/core'
import { SortableContainer } from 'react-sortable-hoc'
import { FailableImage, ZoomableGallery } from './SortableList'
import ImageHelpers from './Helpers'
import { BaseSortableImageListProps } from './sortableImageListTypes'
import { FadingScrollContainer } from './FadingScrollContainer'
import useBreakpoint from '../../hooks/useBreakpoint'

interface MinimalSortableItemProps {
  loading: boolean
  url: string // the url of the image, or preview url
  ii: number
  rotation: number
  progress: number
  isDisabled: boolean
  setCurrentImageIndex: (index) => void
  hidden?: boolean
  failed?: boolean
}

const MinimalImageItem: React.FC<MinimalSortableItemProps> = ({
  loading,
  progress,
  url,
  rotation,
  ii, // the item index
  isDisabled,
  setCurrentImageIndex,
  hidden = false,
  failed = false
}) => {
  const hackRotation = React.useMemo(() => {
    return ImageHelpers.hackRotationFn(url)
  }, [url])
  const className = CX('img-container-minimal', hidden ? '' : 'active')

  if (failed) {
    return (
      <div className={className}>
        <p className={Classes.TEXT_MUTED}>Image Upload Failed</p>
      </div>
    )
  }

  return (
    <div className={className} onClick={() => !loading && !isDisabled && !hidden && setCurrentImageIndex(ii)}>
      {loading ? (
        <Spinner value={progress} />
      ) : (
        <FailableImage
          src={url}
          className="img-selection-img"
          alt={`number ${ii + 1}`}
          style={{
            transform: `translatez(0) rotate(${-Math.abs(hackRotation - rotation || 0)}deg)`
          }}
        />
      )}
      {ii === 0 && !loading && !hidden && (
        <Tag className="main-img-tag-minimal" intent={Intent.PRIMARY}>
          Main Image
        </Tag>
      )}
    </div>
  )
}

export const MinimalSortableImageList = SortableContainer(
  ({
    targetElementSelectorForSticky = '#image-uploader-container',
    items,
    isDisabled = false,
    children
  }: BaseSortableImageListProps) => {
    const [currentImageIndex, setCurrentImageIndex] = useState()

    const innerContainerRef = useRef<HTMLDivElement>()

    const stickyBreakpoint = useBreakpoint(targetElementSelectorForSticky)

    return (
      <FadingScrollContainer innerContainerRef={innerContainerRef} hidden={!stickyBreakpoint}>
        <div ref={innerContainerRef} className={CX('image-gallery-container-minimal')}>
          <ZoomableGallery
            currentImageIndex={currentImageIndex}
            setCurrentImageIndex={setCurrentImageIndex}
            items={items}
          />
          {children}
          {items.map((f, i) => {
            return (
              <MinimalImageItem
                key={`${f.id || f.key || f.preview || i}-minimal`}
                loading={f.loading}
                url={f.url || f.preview}
                ii={i}
                rotation={f.rotation}
                progress={f.progress}
                isDisabled={isDisabled}
                hidden={!stickyBreakpoint}
                setCurrentImageIndex={setCurrentImageIndex}
                failed={f.failed}
              />
            )
          })}
        </div>
      </FadingScrollContainer>
    )
  }
)
